export interface ShopEditApprovalFieldChange {
  field?: string;
  changed?: boolean;
  newValue?: any;
}

export interface ShopEditApprovalFieldConfig {
  field?: string;
  code?: string;
  controlType?: string;
  selectionList?: Array<{
    value?: any;
    name?: string;
  }>;
}

export interface ShopEditApprovalFieldResult {
  changed: boolean;
  value?: any;
}

export interface ShopEditApprovalFieldStyle {
  labelStyle?: { color: string };
  valueStyle?: undefined;
}

/** 根据门店详情字段配置匹配审批快照中的字段变更。 */
export const getShopEditApprovalField = (
  fieldChanges: ShopEditApprovalFieldChange[] = [],
  config: ShopEditApprovalFieldConfig = {},
): ShopEditApprovalFieldResult => {
  const change = fieldChanges.find(item => item.field === config.field || item.field === config.code);
  if (!change) {
    return { changed: false };
  }

  return {
    changed: Boolean(change.changed),
    value: change.changed ? change.newValue : undefined,
  };
};

/** 获取审批快照或门店详情中当前实际生效的字段值。 */
export const getShopEditApprovalFieldRawValue = (
  fieldChanges: ShopEditApprovalFieldChange[] = [],
  config: ShopEditApprovalFieldConfig = {},
  detail: any = {},
) => {
  const approvalField = getShopEditApprovalField(fieldChanges, config);
  if (approvalField.changed) {
    return approvalField.value;
  }
  return detail[config.field || ''];
};

/** 获取审批快照或门店详情中的附件资源。 */
export const getShopEditApprovalFieldAssets = (
  fieldChanges: ShopEditApprovalFieldChange[] = [],
  config: ShopEditApprovalFieldConfig = {},
  detail: any = {},
) => {
  const value = getShopEditApprovalFieldRawValue(fieldChanges, config, detail);
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    try {
      const assets = JSON.parse(value);
      return Array.isArray(assets) ? assets : [];
    } catch (error) {
      console.log(error);
    }
  }
  return [];
};

/** 将所属商圈评分兼容转换为详情组件使用的对象格式。 */
export const getMatchModelClusterScore = (value: any) => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    console.log(error);
    return undefined;
  }
};

/** 按门店字段配置的 controlType 格式化审批值或原详情值。 */
export const getShopEditApprovalFieldValue = (
  fieldChanges: ShopEditApprovalFieldChange[] = [],
  config: ShopEditApprovalFieldConfig = {},
  detail: any = {},
) => {
  const approvalField = getShopEditApprovalField(fieldChanges, config);
  const value = approvalField.changed ? approvalField.value : detail[config.field || ''];
  if (config.code === 'investmentManagerName') {
    return approvalField.changed ? value : detail.investmentManagerName;
  } else if (config.controlType === 'area' || config.code === 'franchiseeName') {
    return approvalField.changed ? value : detail[config.code || ''];
  } else if (config.controlType === 'dropDownRadio') {
    const result = config.selectionList?.find(item => String(item.value) === String(value));
    // 有值但是该选项已被删除时，展示以前设置选项的名称（门店类型选项被停用的情况）
    return result ? result.name : ((approvalField.changed ? value : detail[config.code || '']) || '');
  } else if (config.controlType === 'fileUpload') {
    return [];
  } else if (config.controlType === 'other' && config.code === 'matchModelClusterScore') {
    return getMatchModelClusterScore(value)?.clusterScore || '';
  }
  return value;
};

/** 审批快照变更字段仅标记字段名称，保持字段值沿用详情组件样式。 */
export const getShopEditApprovalFieldStyle = (changed: boolean): ShopEditApprovalFieldStyle => ({
  labelStyle: changed ? { color: '#F53F3F' } : undefined,
  valueStyle: undefined,
});
